import { Button, Listbox, Popover } from '@shopify/polaris';
import { ClockMajor } from '@shopify/polaris-icons';
import { formatDateTime, isValidTime } from '@utils/dates';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ButtonContainer } from '../dropdown';
import {
  DatePickerContainer,
  PickerButtonActivator,
  PickerButtonProps,
} from './common';

export type Time = {
  hours: number;
  minutes: number;
};

export type TimePickerProps = Omit<PickerButtonProps, 'icon' | 'value'> & {
  date: Date;
  onChange: (value: Time) => void;
  step: number;
  value: string;
};

const formatTime = ({ hours, minutes }: Time) => {
  return `${hours > 12 ? hours - 12 : hours === 0 ? hours + 12 : hours}:${
    minutes.toString().length < 2 ? `0${minutes}` : minutes
  } ${hours === 24 || hours < 12 ? `AM` : `PM`}`;
};

const computeSteps = (step: number) => {
  const minutesInaDay = 60 * 24;
  if (step < 0 || step > minutesInaDay) {
    throw new Error('Invalid step value');
  }
  const steps = minutesInaDay / step;
  const result: Time[] = [{ hours: 0, minutes: 0 }];
  let sumTime = 0;
  for (let i = 1; i <= steps; i++) {
    sumTime = sumTime + step;
    const hours = Math.floor(sumTime / 60);
    const minutes = sumTime % 60;
    if (hours < 24) {
      result.push({ hours, minutes });
    }
  }
  return result;
};

const filterStepsByDate = (date: Date, step: number) => {
  let steps = computeSteps(step);
  const now = new Date();
  if (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  ) {
    const dateTime = convertStringToTime(formatDateTime(now).time);
    steps = [
      dateTime,
      ...steps.filter(
        ({ hours, minutes }) =>
          hours > dateTime.hours ||
          (hours === dateTime.hours && minutes > dateTime.minutes),
      ),
    ];
  }
  return steps;
};

export const convertStringToTime = (value: string): Time => {
  const [hoursStr, minuteWithPeriod] = value.split(':');
  const [minutes, period] = minuteWithPeriod.split(' ');
  const hours = Number(hoursStr);
  return {
    hours:
      period === 'PM'
        ? hours < 12
          ? hours + 12
          : hours
        : hours === 12
        ? 0
        : hours,
    minutes: Number(minutes),
  };
};

export const nearestTimeValueInFuture = (value: string, step: number): Time => {
  const time = convertStringToTime(value);
  const totalMinutes = time.hours * 60 + time.minutes;
  const diff = totalMinutes % step;
  return { hours: time.hours, minutes: time.minutes + step - diff };
};

export const TimePickerContainer = styled(DatePickerContainer)`
  .Polaris-Button {
    padding: 16px;
  }
  .Polaris-Connected {
    align-items: center;
  }

  .Polaris-Button:hover input {
    background: var(--p-action-secondary-hovered);
    background-image: linear-gradient(rgba(0, 0, 0, 0.2) 0 0);
  }

  input {
    padding: 0;
    text-align: center;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #202223;
    border: transparent;
    outline: none;
    min-height: auto;
    width: 75px;

    &:hover {
      background: var(--p-action-secondary-hovered);
      background-image: linear-gradient(rgba(0, 0, 0, 0.2) 0 0);
    }

    &:focus {
      border-bottom: 1px solid;
    }
  }
`;

const TimePicker: FunctionComponent<TimePickerProps> = ({
  date,
  disabled,
  onChange,
  step = 30,
  title,
  value: parentValue,
}) => {
  const inputRef = useRef<any>();
  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState(filterStepsByDate(date, step));
  const [inputValue, setInputValue] = useState(parentValue);

  useEffect(() => {
    setInputValue(parentValue);
  }, [parentValue]);

  useEffect(() => {
    setSteps(filterStepsByDate(date, step));
  }, [date, step]);

  const onValue = (newValue: string) => {
    let time = parentValue;
    if (isValidTime(newValue)) {
      ({ time } = formatDateTime(
        new Date(`${new Date().toDateString()} ${newValue}`),
      ));
    }
    setInputValue(time);
    onChange(convertStringToTime(time));
  };

  const onClick = () => {
    setIsActive(true);
    inputRef.current?.focus();
  };

  const onInputChange = (value: string) => {
    setInputValue(value);
  };

  const onClosePopOver = () => {
    onClose(inputValue);
  };

  const onClose = (value: string) => {
    setIsActive(false);
    inputRef.current?.blur();
    onValue(value);
  };

  const onChangeItem = (index: string) => {
    const selectedIndex = Number(index);
    const value = formatTime(steps[selectedIndex]);
    onClose(value);
  };

  const activator = (
    <ButtonContainer>
      <Button
        onClick={onClick}
        disabled={disabled}
        disclosure={false}
        fullWidth={true}
      >
        {
          (
            <PickerButtonActivator
              icon={{ source: ClockMajor }}
              title={title}
              value={
                <input
                  ref={inputRef}
                  value={inputValue}
                  onBlur={onClosePopOver}
                  onChange={(event) => onInputChange(event.target.value)}
                />
              }
            />
          ) as unknown as string
        }
      </Button>
    </ButtonContainer>
  );
  return (
    <TimePickerContainer>
      <Popover
        active={isActive}
        activator={activator}
        onClose={onClosePopOver}
        ariaHaspopup={false}
        sectioned
        fullWidth
        preferInputActivator={false}
        preventFocusOnClose
        autofocusTarget="none"
      >
        <Listbox onSelect={onChangeItem}>
          {steps.map((element, index) => (
            <Listbox.Option key={index} value={index.toString()}>
              {formatTime(element)}
            </Listbox.Option>
          ))}
        </Listbox>
      </Popover>
    </TimePickerContainer>
  );
};

export default TimePicker;
